import { useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import type { JWMView } from '@atlassian/jira-business-common/src/common/types/jwm-view.tsx';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common/src/controllers/business-entity-context/index.tsx';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { HIERARCHY_LEVELS_LOCAL_STORAGE_KEY } from './constants';
import { getLocalStorage } from './utils';

export const useHierarchyCache = ({ view }: { view: JWMView }) => {
	const {
		loading,
		error,
		hierarchyLevels: newHierarchyLevels,
	} = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	const { data: entity } = useBusinessEntity();
	const localStorage = getLocalStorage(view);

	const hierarchyStorageKey = `${entity.id}_${HIERARCHY_LEVELS_LOCAL_STORAGE_KEY}`;
	const [hierarchyLevels, setHierarchyLevels] = useState(localStorage.get(hierarchyStorageKey));

	// save hierarchyLevels in localStorage
	useEffect(() => {
		if (!loading && !error) {
			if (!isEqual(hierarchyLevels, newHierarchyLevels)) {
				setHierarchyLevels(newHierarchyLevels);
				localStorage.set(hierarchyStorageKey, newHierarchyLevels);
			}
		}
	}, [
		loading,
		error,
		hierarchyLevels,
		hierarchyStorageKey,
		setHierarchyLevels,
		localStorage,
		newHierarchyLevels,
	]);

	const hierarchyLevelsWithCache = useMemo(() => {
		// return from cache (localStorage)
		if (hierarchyLevels && Array.isArray(hierarchyLevels)) {
			return hierarchyLevels;
		}
		// returns nothing if available fields query is in progress. But if cache is empty, it returns newHierarchyLevels
		if (loading) {
			return [];
		}
		return newHierarchyLevels;
	}, [hierarchyLevels, loading, newHierarchyLevels]);

	return { hierarchyLevelsWithCache };
};
